import { CostCentersEmployeesStructuredAssignmentsQuery } from 'generated/resources/graphql'

import { StructuredAssignment } from 'modules/cost_centers/components/Resources/Employees/types'

import { ACTIVE, TERMINATED } from './constants'
import { TextValue } from './labels'
import { Filters, FormattedCostCenterEmployeeWithStructuredAssignment } from './types'

/**
 * Build a display name for a structured cost center from its dimensions.
 * Joins all non-null dimension names (family, function, role, workplace) with ' - '.
 * Falls back to the CC name if no dimensions are present.
 */
export const getStructuredCostCenterName = (costCenter: {
  code?: string | null
  name: string
  dimensions?: Array<{
    jobCatalogFamilyName?: string | null
    jobCatalogFunctionName?: string | null
    jobCatalogRoleName?: string | null
    workplaceName?: string | null
  }> | null
}): string => {
  const parts: string[] = []

  if (costCenter.dimensions && costCenter.dimensions.length > 0) {
    for (const dimension of costCenter.dimensions) {
      if (dimension.jobCatalogFamilyName) parts.push(dimension.jobCatalogFamilyName)
      if (dimension.jobCatalogFunctionName) parts.push(dimension.jobCatalogFunctionName)
      if (dimension.jobCatalogRoleName) parts.push(dimension.jobCatalogRoleName)
      if (dimension.workplaceName) parts.push(dimension.workplaceName)
    }
  }

  const dimensionName = parts.length > 0 ? parts.join(' - ') : costCenter.name
  return costCenter.code ? `${costCenter.code} - ${dimensionName}` : dimensionName
}

/**
 * Build a combined CC name for a single assignment from its costCenter dimensions.
 */
const getAssignmentCostCenterName = (assignment: StructuredAssignment): string => {
  const costCenter = assignment.costCenter
  if (!costCenter) return ''
  return getStructuredCostCenterName(costCenter)
}

/**
 * Build the "Current Assignments" label for a parent row.
 * - If 1 assignment: show the combined CC name directly.
 * - If >1 assignments: show the count.
 * - If 0 assignments: show N/A placeholder.
 */
const currentAssignmentLabelForParent = (
  edges: Array<{ node: StructuredAssignment }>
): TextValue => {
  if (edges.length === 0) {
    return { placeholder: '-' }
  }

  if (edges.length === 1) {
    const firstEdge = edges[0]
    if (!firstEdge) return { placeholder: '-' }
    const name = getAssignmentCostCenterName(firstEdge.node)
    return name ? { text: name } : { placeholder: '-' }
  }

  return { text: edges.length.toString() }
}

/**
 * Build the "Current Assignments" label for a child row — always the combined CC name.
 */
const currentAssignmentLabelForChild = (assignment: StructuredAssignment): TextValue => {
  const name = getAssignmentCostCenterName(assignment)
  return name ? { text: name } : { placeholder: '-' }
}

export const mapEmployeeData = (data: CostCentersEmployeesStructuredAssignmentsQuery) => {
  const edges = data.employees.employeesConnection.edges
  return edges.map(({ node }) => ({
    ...node,
    currentAssignmentLabel: currentAssignmentLabelForParent(node.structuredAssignments.edges),
    startDate: node.structuredAssignments.edges[0]?.node?.startDate,
    percentage: node.structuredAssignments.edges.reduce(
      (acc, value) => acc + Number(value.node.percentage),
      0
    ),
  }))
}

export const mapChildrenData = (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
  return Promise.resolve({
    records: item.structuredAssignments.edges.map(({ node }) => ({
      ...item,
      currentAssignmentLabel: currentAssignmentLabelForChild(node),
      percentage: Number(node.percentage),
      startDate: '',
      isChild: true,
    })),
    type: 'detailed' as const,
  })
}

const filterByAssignmentStatus = (assignmentStatus?: boolean[]) => {
  if (
    assignmentStatus === undefined ||
    assignmentStatus.length === 0 ||
    (assignmentStatus.includes(true) && assignmentStatus.includes(false))
  ) {
    return {}
  }

  return { withCostCenterAssignments: assignmentStatus.includes(true) }
}

const filterByPeopleStatus = (status?: string[]) => {
  if (!status?.length || (status.includes(TERMINATED) && status.includes(ACTIVE))) {
    return {}
  }

  return { accessStatus: status }
}

export const mapVariables = ({
  search,
  sortings,
  filters,
}: {
  search?: string
  filters?: Filters
  sortings?: Array<{ field: string; order: string }>
}) => {
  const locationIds = filters?.locationIds

  return {
    search: search,
    sortOrder: sortings?.[0],
    legalEntityIds: filters?.legalEntityIds?.length ? filters?.legalEntityIds : undefined,
    teamIds: filters?.teamIds?.length ? filters?.teamIds : undefined,
    costCenterIds: filters?.costCenterIds?.length ? filters?.costCenterIds : undefined,
    employeeIds: filters?.employeeIds?.length ? filters?.employeeIds : undefined,
    ...(locationIds?.length && { locationIds }),
    ...filterByAssignmentStatus(filters?.withCostCenterAssignments),
    ...filterByPeopleStatus(filters?.peopleStatus),
    // Job catalog role filter (tree-only, structured CC always uses new JC)
    ...(filters?.jobRole?.length && {
      referenceContractTreeLevelUuids: filters.jobRole.map(String),
    }),
  }
}
